/**
 * CancellationView
 * 
 * Cancellation Policy page for Plot Planet.
 */

import { PolicyView } from './PolicyView'
import { BRANDING } from '../config/branding'

export function CancellationView() {
  return (
    <PolicyView title="Cancellation Policy" lastUpdated="January 20, 2026">
      <h2>1. Overview</h2>
      <p>
        You may cancel your {BRANDING.name} subscription or delete your account at any time. 
        This policy explains what happens when you cancel and your options for managing your 
        subscription.
      </p>

      <h2>2. How to Cancel Your Subscription</h2>
      
      <h3>2.1 Through Your Account</h3>
      <p>To cancel your subscription:</p>
      <ol>
        <li>Log in to your {BRANDING.name} account</li>
        <li>Navigate to Account Settings or Payment Settings</li>
        <li>Click "Cancel Subscription" or "Manage Subscription"</li>
        <li>Follow the prompts to confirm cancellation</li>
      </ol>

      <h3>2.2 By Email</h3>
      <p>
        You may also cancel by emailing <a href="mailto:support@plotplanet.com">support@plotplanet.com</a> with 
        the subject line "Cancel Subscription" and including your account email address. We will 
        process your cancellation within 1-2 business days and send confirmation.
      </p>

      <h2>3. What Happens After Cancellation</h2>
      
      <h3>3.1 Subscription Access</h3>
      <p>When you cancel a subscription:</p>
      <ul>
        <li>Your subscription will remain active until the end of your current billing period</li>
        <li>You will not be charged for any future billing periods</li>
        <li>At the end of your billing period, your account will revert to the Free plan</li>
        <li>You will retain access to Free plan features and limits</li>
      </ul>

      <h3>3.2 Your Data</h3>
      <p>After cancellation:</p>
      <ul>
        <li>All your projects and content remain in your account</li>
        <li>Projects exceeding Free plan limits may become read-only</li>
        <li>You can still export your data at any time</li>
        <li>Team collaboration features may be limited based on Free plan restrictions</li>
      </ul>

      <h2>4. Lifetime Access</h2>
      <p>
        Lifetime Access purchases cannot be "cancelled" in the traditional sense since they 
        are one-time payments. Your access remains permanent. However, if you wish to delete 
        your account entirely, see Section 7 below.
      </p>

      <h2>5. Pay-per-Project Purchases</h2>
      <p>
        Individual project purchases are one-time payments and do not have recurring charges 
        to cancel. Your access to purchased projects remains as long as your account is active.
      </p>

      <h2>6. Reactivating Your Subscription</h2>
      <p>
        If you change your mind, you can reactivate your subscription at any time:
      </p>
      <ul>
        <li>Log in to your account and navigate to Payment Settings</li>
        <li>Choose your desired plan</li>
        <li>Enter payment information and confirm</li>
        <li>Your full access will be restored immediately</li>
      </ul>
      <p>
        If you reactivate within the same billing period that you cancelled, you may be able 
        to resume your existing subscription without any gap in service.
      </p>

      <h2>7. Deleting Your Account</h2>
      <p>
        If you wish to permanently delete your account and all associated data:
      </p>
      <ol>
        <li>Cancel any active subscriptions first</li>
        <li>Export any data you wish to keep</li>
        <li>Email <a href="mailto:support@plotplanet.com">support@plotplanet.com</a> requesting account deletion</li>
        <li>We will confirm deletion within 5 business days</li>
      </ol>
      <p>
        <strong>Warning:</strong> Account deletion is permanent. All your projects, characters, 
        images, and other content will be permanently deleted and cannot be recovered.
      </p>

      <h2>8. Data Retention After Cancellation</h2>
      <p>
        When you cancel your subscription (but keep your account):
      </p>
      <ul>
        <li>All your data remains accessible</li>
        <li>We retain your data indefinitely while your account exists</li>
        <li>You can export your data at any time</li>
      </ul>
      <p>
        When you delete your account:
      </p>
      <ul>
        <li>Your data is deleted from our active systems within 30 days</li>
        <li>Backup copies may be retained for up to 90 days for technical reasons</li>
        <li>Anonymized usage data may be retained for analytics purposes</li>
      </ul>

      <h2>9. Cancellation During Free Trial</h2>
      <p>
        If you cancel during a free trial period:
      </p>
      <ul>
        <li>You will not be charged</li>
        <li>Your account will immediately revert to the Free plan</li>
        <li>All your created content remains in your account</li>
      </ul>

      <h2>10. Automatic Renewal</h2>
      <p>
        Subscriptions automatically renew at the end of each billing period unless cancelled. 
        We will send a reminder email before each renewal for annual subscriptions.
      </p>

      <h2>11. Contact Us</h2>
      <div className="policy-contact">
        <p>
          For cancellation assistance or questions about this policy, please contact us:
        </p>
        <a href="mailto:support@plotplanet.com">support@plotplanet.com</a>
        <a href="mailto:hello@plotplanet.com">hello@plotplanet.com</a>
      </div>
    </PolicyView>
  )
}

export default CancellationView
